/**
 * Кнопка с удержанием. docs/ui-design.md §8: это предохранитель, а не украшение — ровно
 * поэтому её нельзя «нажать мимо», и ровно поэтому обычный `click` здесь не подтверждает
 * ничего.
 *
 * Три решения, которые стоит объяснить:
 *
 * 1. **Время меряется тиками таймера, а не часами.** `nowMs()` внутри не вызывается вовсе:
 *    прогресс — это номер тика, а завершение — отдельный `setTimeout`. Так удержание
 *    остаётся проверяемым (`vi.useFakeTimers`) и не зависит от того, что вкладку усыпили
 *    посреди нажатия. Плюс правило R11 (единственный дом времени) выполняется само собой.
 *
 * 2. **Слушаем и pointer-, и mouse-события.** `start`/`cancel` идемпотентны, поэтому пара
 *    pointerdown+mousedown безопасна. Причина не в браузерах, а в тестовой среде: jsdom
 *    отдаёт `PointerEvent` не во всех сборках, и потеря удержания в тестах выглядела бы как
 *    «предохранитель не работает».
 *
 * 3. **Клавиатура.** Пробел и Enter удерживают так же, как палец; `onClick` глушится, иначе
 *    синтетический клик от пробела подтверждал бы действие мгновенно.
 */

import {
  type JSX,
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import css from './HoldToConfirm.module.css';

/** 800 мс — значение из docs/ui-design.md §8 и из макета (подпись под кнопкой). */
export const HOLD_MS = 800;
const TICK_MS = 40;

const R = 9;
const CIRC = 2 * Math.PI * R;

export interface HoldToConfirmProps {
  /** Что произойдёт по завершении удержания — фразой, а не глаголом-командой. */
  readonly label: string;
  readonly disabled?: boolean;
  readonly onConfirm: () => void;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function HoldToConfirm({
  label,
  disabled = false,
  onConfirm,
}: HoldToConfirmProps): JSX.Element {
  const [progress, setProgress] = useState(0);
  const [holding, setHolding] = useState(false);
  const doneTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const tickTimer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const stopTimers = useCallback(() => {
    if (doneTimer.current !== undefined) {
      clearTimeout(doneTimer.current);
      doneTimer.current = undefined;
    }
    if (tickTimer.current !== undefined) {
      clearInterval(tickTimer.current);
      tickTimer.current = undefined;
    }
  }, []);

  // Таймеры не должны пережить размонтирование: диалог закрывается по успеху действия,
  // и сработавший после этого onConfirm отправил бы второй запрос.
  useEffect(() => stopTimers, [stopTimers]);

  const cancel = useCallback(() => {
    if (doneTimer.current === undefined && tickTimer.current === undefined) return;
    stopTimers();
    setHolding(false);
    setProgress(0);
  }, [stopTimers]);

  const start = useCallback(() => {
    if (disabled) return;
    if (doneTimer.current !== undefined) return; // уже держим — второе событие игнорируем
    setHolding(true);
    setProgress(0);
    if (!prefersReducedMotion()) {
      let ticks = 0;
      tickTimer.current = setInterval(() => {
        ticks += 1;
        setProgress(Math.min(1, (ticks * TICK_MS) / HOLD_MS));
      }, TICK_MS);
    }
    doneTimer.current = setTimeout(() => {
      stopTimers();
      setHolding(false);
      setProgress(0);
      onConfirm();
    }, HOLD_MS);
  }, [disabled, onConfirm, stopTimers]);

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key !== ' ' && e.key !== 'Enter') return;
    e.preventDefault();
    if (e.repeat) return;
    start();
  };

  const onKeyUp = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key !== ' ' && e.key !== 'Enter') return;
    cancel();
  };

  const swallowClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
  };

  return (
    <button
      type="button"
      className={`hold ${css['btn']} ${disabled ? css['btnDisabled'] : ''}`}
      disabled={disabled}
      aria-disabled={disabled}
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onMouseDown={start}
      onMouseUp={cancel}
      onMouseLeave={cancel}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onBlur={cancel}
      onClick={swallowClick}
    >
      <svg className={css['ring']} width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
        <circle className={css['track']} cx="11" cy="11" r={R} fill="none" strokeWidth="2" />
        <circle
          className={css['fill']}
          cx="11"
          cy="11"
          r={R}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={CIRC}
          strokeDashoffset={CIRC * (1 - progress)}
        />
      </svg>
      <span>{holding ? `Удерживайте — ${label}` : label}</span>
    </button>
  );
}
